import http from "./axios";
import { handleError } from "./http-utils.service";
import Cookies from "universal-cookie";
import moment from "moment";
import User from "../interfaces/user.interface";
import { UserFormType } from "../interfaces/user.interface";
import {
  Pagination,
  PaginateCourseStaff,
} from "../interfaces/pagination.interface";
import CourseStaff from "../interfaces/course-staff.interface";
import UsersTokens from "../interfaces/users-tokens.interface";

let cookies = new Cookies();

type UserResponse = {
  success: boolean;
  data: {
    user: User;
  };
};
interface PropsI extends Pagination {
  courseId?: number;
  role?: string;
}

interface LoggedInApi {
  isLogged: boolean;
  user?: User;
}
export const logout = () => {
  cookies.remove("access_token", {
    path: "/",
    domain: process.env.REACT_APP_DOMAIN,
  });
};

export const getAllProctors = async (props: PropsI) => {
  const { limit, skip, query, role, courseId } = props;
  const options = {
    params: {
      limit,
      skip,
      courseId,
      role,
      ...(query ? { search: query } : {}),
    },
  };
  try {
    const response = await http.get<{ count: number; list: [User] }>(
      `/users/proctors`,
      options,
    );
    return response.data;
  } catch (e) {
    throw handleError(e);
  }
};
export const getAllStaff = async (props: PropsI) => {
  const { limit, skip, query, role, courseId } = props;
  const options = {
    params: {
      limit,
      skip,
      courseId,
      role,
      ...(query ? { search: query } : {}),
    },
  };
  try {
    const response = await http.get<{ count: number; list: [User] }>(
      `/users/staff`,
      options,
    );
    return response.data;
  } catch (e) {
    throw handleError(e);
  }
};

export const getCourseStaff = async (props: PaginateCourseStaff) => {
  const { limit, skip, query } = props;
  const options = {
    params: {
      limit,
      skip,
      ...(query ? { query } : {}),
    },
  };
  try {
    const response = await http.get<{ count: number; list: [CourseStaff] }>(
      `/courses-staff/courses/${props.courseId}`,
      options,
    );
    return response.data;
  } catch (e) {
    throw handleError(e);
  }
};

export const login = async (props: { password: string; email: string }) => {
  const response = await http.post<{ accessToken: string; user: User }>(
    `/users/auth`,
    props,
  );
  if (response.data.accessToken && response.data.user.status) {
    const { accessToken } = response.data;
    const expiration = moment(moment()).add(24, "hours").toDate();
    cookies.set("access_token", accessToken, {
      path: "/",
      domain: process.env.REACT_APP_DOMAIN,
      expires: expiration,
      // httpOnly: true,
      secure: false,
    });
  }
  return response.data;
};

export const isLoggedIn = async () => {
  if (cookies.get("access_token")) {
    const response = await http.post<LoggedInApi>(`/users/isLogged`);
    return response.data ? response.data : { isLogged: false };
  }
  return { isLogged: false };
};

export const createUser = async (props: UserFormType): Promise<User> => {
  const response = await http.post<UserResponse>(`/users`, props);
  return response.data.data.user;
};

export const updateUser = async (props: Partial<User>): Promise<User> => {
  const response = await http.patch<UserResponse>(`/users/${props.id}`, props);
  return response.data.data.user;
};

export const updatePassword = async (props: UsersTokens | undefined) => {
  const response = await http.put<{ success: boolean; message: string }>(
    `/users/${props?.userId}`,
    props,
  );
  return response.data;
};

export const resendUserActivation = async (id: number) => {
  const response = await http.get<{ success: boolean; message: string }>(
    `/users/${id}/activation`,
  );
  return response.data;
};

export const resendEmailConfirmation = async (id: number) => {
  const response = await http.get<{ success: boolean; message: string }>(
    `/users/${id}/confirmation`,
  );
  return response.data;
};

export const validateUser = async (token: string) => {
  const response = await http.get<{ success: boolean; result: User }>(
    `/users/validate/${token}`,
  );
  return response.data;
};

export const getProfilePicture = async (id: number) => {
  const response = await http.get<{ url: string }>(
    `/users/${id}/profile-picture`,
  );

  return response.data;
};

export const updateProfilePicture = async (
  id: number,
  formData: FormData,
): Promise<User> => {
  const response = await http.patch<UserResponse>(`/users/${id}`, formData, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });
  return response.data.data.user;
};

export const updateLoginInformation = async (props: {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
}): Promise<User> => {
  const response = await http.patch<UserResponse>(
    `/users/${props.id}/email`,
    props,
  );
  return response.data.data.user;
};

export const getUserDetails = async (userId: string) => {
  const response = await http.get<User>(`/users/${userId}`);
  return response.data;
};
